// ---------------------------------------------------------------------------------------------
// PERF-1 — **ONE ANSWER TO *WHO AM I IN THIS ROOM*, SHARED BY EVERYBODY WHO ASKS AT ONCE.**
//
// **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180): `/api/living-room/me` WAS FETCHED EIGHT TIMES ON
// ONE LIVING ROOM LOAD**, ~500ms each, against a FIVE-CONNECTION pool (13-F1's standing warning).
// Four of the eight were an effect loop — `requireMembership` was a new function on every App render,
// which recreated `gate`, which recreated `loadMe`, which re-fired the mount effect — and that is
// repaired at its cause in `App.tsx` rather than papered over here. **The remaining four are
// LEGITIMATE:** the feed asks, `HouseInbox` asks, and the inbox screen asks, because each of them
// genuinely needs her seat. Three components needing one fact is not a defect; three REQUESTS for it
// is (R28/R29).
//
// >>> IT COALESCES ONLY WHAT IS IN FLIGHT, AND CARRIES NO CACHE AT ALL. <<< A TTL would be faster
// and would introduce a question nobody can answer from the outside — *is this answer still true* —
// on a payload that carries her tier, her strike state and her spell counter. **Sharing a promise
// that has not resolved yet cannot be stale by construction**, because every sharer was going to
// receive that exact answer anyway. The moment it settles the slot is cleared, so the next read is a
// real one. That is the whole safety argument, and it is why this file has no expiry logic to get
// wrong.
//
// A caller that must not share — one re-reading straight after a WRITE, where the point is to see
// what changed — passes `{ fresh: true }` and is never given somebody else's in-flight answer.
// ---------------------------------------------------------------------------------------------

// Keyed by URL rather than a bare singleton, so a second room read (a different query string) can
// never be handed the first one's answer. One slot per distinct question.
//
// **IT IS GENERIC, AND THAT IS NOT A CONVENIENCE.** This function's job is COALESCING, not typing:
// narrowing every caller's answer to one shape here would push a cast into three call sites, and a
// cast is where a payload's real shape stops being checked. The reader hands back exactly what its
// own `doRead` hands back.
const inFlight = new Map<string, Promise<unknown>>();

/**
 * Read a room endpoint, sharing an identical request that has not answered yet.
 *
 * `doRead` is a PARAMETER rather than an imported `json`, so a `.mjs` gate RUNS this with a counting
 * reader and can prove the coalescing rather than reading it (R46's locked-door shape). This module
 * imports NOTHING, which is the entry requirement that lets a gate load it at all.
 */
export function sharedRoomRead<T>(
  url: string,
  doRead: (u: string) => Promise<T>,
  { fresh = false }: { fresh?: boolean } = {},
): Promise<T> {
  if (fresh) return doRead(url);
  const running = inFlight.get(url) as Promise<T> | undefined;
  if (running) return running;
  // **THE SLOT IS CLEARED IN A `finally`, INCLUDING ON A THROW.** A rejected read left parked here
  // would hand its rejection to every future caller forever — one bad minute turning into a room
  // that never loads again, which is worse than the defect this exists for (R19's silent direction).
  const p = (async () => {
    try {
      return await doRead(url);
    } finally {
      inFlight.delete(url);
    }
  })();
  inFlight.set(url, p);
  return p;
}

/** How many reads are parked right now. Exported so a gate can assert the slot really empties. */
export function pendingRoomReads(): number {
  return inFlight.size;
}
